import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  boxShadow,
  mediaHelper,
  MOBILE_MAX,
  paletteHelper,
  radiusHelper,
  TABLET_MAX,
} from '@property.com.au/ui-kit';
import {
  desktopAdHeight,
  desktopAdWidth,
  mapDesktopSlotConfigFromVariant,
} from './helpers/mapDesktopSlotConfigFromVariant';
import {
  mapMobileSlotConfigFromVariant,
  mobileAdHeight,
  mobileAdWidth,
} from './helpers/mapMobileSlotConfigFromVariant';
import {
  mapTabletSlotConfigFromVariant,
  tabletAdHeight,
  tabletAdWidth,
} from './helpers/mapTabletSlotConfigFromVariant';
import { DefineAdSlot } from '../../../app-kit/components/DefineAdSlot/DefineAdSlot';

const AdvertisementContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Spacer = styled.div`
  height: ${mobileAdHeight}px;

  ${mediaHelper('tabletMin')} {
    height: ${tabletAdHeight}px;
  }

  ${mediaHelper('desktopMin')} {
    height: ${desktopAdHeight}px;
  }
`;

const slotWrapperCommonStyling = css`
  border-radius: ${radiusHelper('large')};
  overflow: clip;
  ${boxShadow.defaultBoxShadow}
  background-color: ${paletteHelper('backgroundSecondary')};
`;

const MobileSlotWrapper = styled.div`
  width: ${mobileAdWidth}px;
  height: ${mobileAdHeight}px;

  ${slotWrapperCommonStyling}
`;

const TabletSlotWrapper = styled.div`
  ${mediaHelper('mobile')} {
    display: none;
  }

  width: ${tabletAdWidth}px;
  height: ${tabletAdHeight}px;

  ${slotWrapperCommonStyling}
`;

const DesktopSlotWrapper = styled.div`
  ${mediaHelper('tabletMax')} {
    display: none;
  }

  width: ${desktopAdWidth}px;
  height: ${desktopAdHeight}px;

  ${slotWrapperCommonStyling}
`;

export type AdvertisementBrickAdVariant = 'FOR_SALE' | 'FOR_RENT' | 'OFF_MARKET' | 'SOLD';

interface AdvertisementBrickProps {
  variant: AdvertisementBrickAdVariant;
}

export const AdvertisementBrick: FunctionComponent<AdvertisementBrickProps> = ({ variant }) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <AdvertisementContainer>
      {/* Reserve the space during SSR to avoid layout shift */}
      {!isClient && <Spacer />}
      {/* Define ad slot during CSR so only the right sized ad slot is created */}
      {isClient && window.innerWidth <= MOBILE_MAX && (
        <MobileSlotWrapper data-testid="AdvertismentBrickMobileAd">
          <DefineAdSlot slotConfig={mapMobileSlotConfigFromVariant(variant)} />
        </MobileSlotWrapper>
      )}
      {isClient && window.innerWidth > MOBILE_MAX && window.innerWidth <= TABLET_MAX && (
        <TabletSlotWrapper data-testid="AdvertismentBrickTabletAd">
          <DefineAdSlot slotConfig={mapTabletSlotConfigFromVariant(variant)} />
        </TabletSlotWrapper>
      )}
      {isClient && window.innerWidth > TABLET_MAX && (
        <DesktopSlotWrapper data-testid="AdvertismentBrickDesktopAd">
          <DefineAdSlot slotConfig={mapDesktopSlotConfigFromVariant(variant)} />
        </DesktopSlotWrapper>
      )}
    </AdvertisementContainer>
  );
};
